import { Coordinate, RouteAndImages, RouteData, RouteFileType, RouteImageFiles } from "../util/route-data";
import { convertToRouteArray, setStatus, textFileToString, textToHtml } from "./import-utils";
import { assert } from "../util/assert";
import { computeMileMarker } from "../util/gps";
import { toTitleCase } from "@src/util/strings";

export async function isGpxFile(file: File|ArrayBuffer|NonSharedBuffer, filename?: string): Promise<boolean> {
  if (filename?.endsWith('.gpx')) {
    return true;
  }
  const startOfFile = await textFileToString(file, 1000);
  return (startOfFile.startsWith('<?xml') || startOfFile.startsWith('<?XML')) &&
      (startOfFile.indexOf('<gpx ') > 0);
}

export async function processGpxFile(file: File|ArrayBuffer|NonSharedBuffer, { docLink }: { docLink?: string } = {}): Promise<RouteAndImages> {
  let gpxFileString: string|null = await textFileToString(file);

  const parser = new DOMParser();
  let gpxDoc: Document|null = parser.parseFromString(gpxFileString, 'text/xml');
  // Free kmlFileString from memory.
  gpxFileString = null;

  const gpxEl = gpxDoc.querySelector('gpx');
  if (!gpxEl) {
    throw new Error('No gpx tag found in file.');
  }

  const trackEl = findTrackEl(gpxEl);

  const metadataEl = gpxEl.querySelector('metadata');
  const name = metadataEl?.querySelector('name')?.textContent ?? 'Route';
  const link = docLink ?? metadataEl?.querySelector('link')?.getAttribute('href') ?? undefined;

  const data: RouteData = {
    icons: {},
    route: [],
    spots: [],
    categories: [],
    name: name,
    link,
    totalDistance: 0,
    importedViaUrl: Boolean(docLink),
    fileType: RouteFileType.GPX, 
  };
  

  const trackSegEl = findTrackSeg(trackEl);
  const trackCoords: Coordinate[] = [];
  for (const trkpt of Array.from(trackSegEl.querySelectorAll('trkpt'))) {
    trackCoords.push(getCoordinateFromAttributes(trkpt));
  }

  const {route, totalDistance} = convertToRouteArray(trackCoords);
  data.route = route;
  data.totalDistance = totalDistance;

  const typeToIconIdMap = new Map<string, string>();

  for (const waypoint of Array.from(gpxEl.querySelectorAll('wpt'))) {
    const name = waypoint.querySelector('name')?.textContent ?? '';
    const description = textToHtml(waypoint.querySelector('desc')?.textContent  ?? '');
    
    const sym = waypoint.querySelector('sym')?.textContent
    let type = waypoint.querySelector('type')?.textContent ?? 'uncategorized';
    if (type === 'Dot' && sym === 'Dot') {
      // This happens in Ride With GPS when you enable
      // "Include cues as waypoints"
      // May want to make this stricter.
      type = 'direction';
    }
    const categoryName = toTitleCase(type);
    let iconId = typeToIconIdMap.get(type);
    if (!iconId) {
      iconId = String(typeToIconIdMap.size);
      typeToIconIdMap.set(type, iconId);
      data.icons[iconId] = 'icons/' + getIconFilename(type);
    }
    const coord =  getCoordinateFromAttributes(waypoint);
    const mile = computeMileMarker(data.route, coord);
    data.spots.push({
      name,
      isRouteStep: false,
      description,
      categoryName,
      iconId,
      coord,
      mile,
    });
  }

  // Make start and end spots.
  {
    const routeSpots = [
      [ route[0], route[1], route[2] ],
      [ route[route.length - 3], route[route.length - 2], route[route.length - 1] ]
    ]
    const iconId = 'route';
    data.icons['route'] = 'icons/' + getIconFilename('');
    for (let i = 0; i < routeSpots.length; i++) {
      const [lat, lon, mile] = routeSpots[i];
      data.spots.push({
        name: i === 0 ? 'Start' : 'End',
        isRouteStep: true,
        description: '',
        categoryName: 'Route',
        iconId,
        coord: {lat, lon},
        mile,
      });
    }
  }

  // Build Categories
  const categoryToIcon = new Map<string, string>();
  for (const spot of data.spots) {
    const name = spot.categoryName;
    if (name && !categoryToIcon.has(name)) {
      categoryToIcon.set(name, spot.iconId!);
    }
  }
  data.categories = Array.from(categoryToIcon.entries())
      .map(([name, iconId]) => ({
        name,
        iconId,
      }));

  setStatus('Loading icons');
  const images: RouteImageFiles = {};
  for (const filePath of Object.values(data.icons)) {
    // TODO - if an error occurs, remove it from the dataset.
    let response: Response;
    try {
      response = await fetch(filePath);
    } catch (e: any) {
      continue;
    }
    if (!response.ok) {
      continue;
    }
    const contents = await response.arrayBuffer();
    if (contents.byteLength < 10) {
      continue;
    }
    images[filePath] = contents;
  }
  return { routeData: data, images };
}

function getCoordinateFromAttributes(el: Element): Coordinate {
  const lat = el.getAttribute('lat');
  assert(lat, 'Missing latitude value');
  const lon = el.getAttribute('lon');
  assert(lon, 'Missing longitude value');
  return {lat: Number(lat), lon: Number(lon)};
}

function findTrackEl(gpxEl: Element): Element {
  const trackEls = gpxEl.querySelectorAll('trk');
  if (trackEls.length > 1) {
    // TODO: Allow importing multiple tracks, maybe you can just select one on import.
    throw new Error('Only routes with a single track defined are supported.');
  }
  if (trackEls.length === 0) {
    const routeEls = gpxEl.querySelectorAll('rte');
    if (routeEls.length > 0) {
      throw new Error('File is defined with a "route" instead of a "track", which is not yet supported.');
    } else {
      throw new Error('No route found in file.');
    }
  }
  return trackEls[0];
}

function findTrackSeg(trackEl: Element): Element {
  const trackSegEls = trackEl.querySelectorAll('trkseg');
  if (trackSegEls.length > 1) {
    throw new Error('Found multiple track segments. Only routes with a single track segment are supported.');
  }
  if (trackSegEls.length === 0) {
    throw new Error('Track is missing a track segment defintion.');
  }
  return trackSegEls[0];
}

/**
 * These "type" values come from the types provided by the Ride With GPS
 * gpx files.
 * TODO: iterate through all Ride With GPS types.
 */
export function getIconFilename(type: string): string {
  switch (type) {
    case 'campsite': return 'camp_poly.svg';
    case 'food': return 'apple_poly.svg';
    case 'overlook': return 'binoculars_poly.svg';
    case 'rest_area': return 'picnic-table_poly.svg';
    case 'shelter': return 'house_poly.svg';
    case 'store': return 'shopping-bag_poly.svg';
    case 'direction': return 'directions-sign_poly.svg';
    case 'uncategorized':
    default:
      return 'map-marker-1_poly.svg';
  }
}